// Page translation (Chrome/Safari "Translate to English", the Google app's
// in-page translate) rewrites text in place: the original text node is moved
// inside a generated <font> wrapper. React still holds a reference to that text
// node, so the next commit calls insertBefore/removeChild with a parent that is
// no longer the node's parent and the DOM throws NotFoundError. React cannot
// recover from a throw in the commit phase, so it escapes to the ErrorBoundary
// in App.web.js and the shopper gets the recovery screen — most often on the
// cart screen, mid-checkout. Script injectors (Cloudflare Rocket Loader,
// browser extensions) corrupt the tree the same way.
//
// This guard makes those two calls survivable. Translation only ever *wraps*
// nodes, so the reference node is usually still a descendant of the parent
// React expected; in that case we can honour the exact position React asked for
// by operating on the wrapper standing in for it. Only when the node has left
// the subtree entirely do we degrade — appending rather than throwing.
//
// See Sentry EXPO-WEB-CATALOGUE-5YG (WebKit wording) and -7Z9 (Blink wording).

type RecoveryCounts = {
  insertBefore: number;
  removeChild: number;
};

const recovered: RecoveryCounts = { insertBefore: 0, removeChild: 0 };

// Exposed so tests can assert the recovery branch actually ran, rather than the
// native call happening to succeed.
export function getRecoveryCounts(): RecoveryCounts {
  return { ...recovered };
}

// Walk up from `node` to the ancestor that is a direct child of `parent`. That
// ancestor is the wrapper translation put in `node`'s place, so inserting
// relative to it preserves the position React asked for. Returns null when
// `node` is not in `parent`'s subtree at all.
function directChildOf(parent: Node, node: Node): Node | null {
  let current: Node | null = node;
  while (current !== null && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
}

let installed = false;

export default function installDomGuard(): void {
  if (installed) return;
  if (typeof Node !== "function" || !Node.prototype) return;
  installed = true;

  const nativeInsertBefore = Node.prototype.insertBefore;
  const nativeRemoveChild = Node.prototype.removeChild;

  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    if (child !== null && child.parentNode !== this) {
      recovered.insertBefore += 1;
      const wrapper = directChildOf(this, child);
      if (wrapper !== null) return nativeInsertBefore.call(this, node, wrapper);
      return this.appendChild(node);
    }
    return nativeInsertBefore.call(this, node, child);
  };

  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    const actualParent = child.parentNode;
    if (actualParent !== null && actualParent !== this) {
      // The node is still in the document, just moved into a wrapper. Removing
      // it from where it actually lives is what React meant to happen.
      recovered.removeChild += 1;
      return nativeRemoveChild.call(actualParent, child);
    }
    if (actualParent === null) {
      // Already detached by whatever rewrote the tree — nothing left to do.
      recovered.removeChild += 1;
      return child;
    }
    return nativeRemoveChild.call(this, child);
  };
}
